import { Type } from '@angular/core';
import { DatatablePage } from '@app/shared/datatable-page';
import { InternalSettings } from 'datatables.net';
import { DataTable, jQuery } from '../../../globals';

export default class CustomDatatableExtension<T> {
  createdCallbacks: Function[] = [];

  constructor(
    private componentElement: string,
    public key: string,
    private type: Type<T>
  ) {}

  /**
   * Helper function that creates a new datatable extension.
   *
   * @param componentElement Custom html.
   * @param key Character that is used in the 'dom' property of the datatable.
   * @param name Name that is used to register this extension.
   * @param type The angular component that will be created and attached.
   */
  static create<T>(
    componentElement: string,
    key: string,
    name: string,
    type: Type<T>
  ): CustomDatatableExtension<T> {
    const extension = new CustomDatatableExtension(componentElement, key, type);

    jQuery.fn.dataTable.ext.feature.push(extension.toFeature());
    DataTable.feature.register(name, (settings) => extension.toFeature().fnInit(settings));

    return extension;
  }

  /**
   * Initialisation function that is called by the datatable when the table is initialised.
   *
   * See https://datatables.net/manual/plug-ins/features#dom-feature-letter-initialisation for more
   * information.
   */
  fnInit(settings: InternalSettings) {
    const api = new DataTable.Api(settings);
    const element = $(this.componentElement)[0];

    this.createdCallbacks.forEach((cb) => cb(api, element, this.type));

    return element;
  }

  /**
   * Define a callback that is called when the table is initialised.
   */
  onCreated(cb: Function) {
    this.createdCallbacks.push(cb);
  }

  /**
   * Returns an object that describes the extension in a way the datatable understands.
   */
  toFeature() {
    return {
      cFeature: this.key,
      fnInit: (settings: InternalSettings) => this.fnInit(settings),
    };
  }
}

export class DatatableComponent {
  protected datatablePage: DatatablePage<any>;

  protected get dtInstance() {
    return this.datatablePage.dtInstance;
  }

  setDatatablePage(instance: DatatablePage<any>): void {
    this.datatablePage = instance;
  }
}
